import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import serverURL from '../config';
import { msalInstance } from '../utils/msalInstance';

const AuthContext = createContext(null);
const unauthenticatedState = {
    userId: null,
    role: null,
    email: null,
    name: null,
    isAuthenticated: false,
    isLoading: false,
};

export const AuthProvider = ({ children }) => {
    const [authState, setAuthState] = useState({
        ...unauthenticatedState,
        isLoading: true,
    });

    const navigate = useNavigate();

    const checkAuthStatus = useCallback(async () => {
        try {
            const response = await axios.get(`${serverURL}/auth/status`, { withCredentials: true });
            if (response.data.isAuthenticated) {
                setAuthState({
                    userId: response.data.user.userId,
                    role: response.data.user.role,
                    email: response.data.user.email,
                    name: response.data.user.name || null,
                    isAuthenticated: true,
                    isLoading: false,
                });
            } else {
                setAuthState(unauthenticatedState);
            }
        } catch (err) {
            const statusCode = err.response?.status;
            if (statusCode !== 401 && statusCode !== 403) {
                console.error('Auth status check failed:', err);
            }
            setAuthState(unauthenticatedState);
        }
    }, []);

    useEffect(() => {
        checkAuthStatus();
    }, [checkAuthStatus]);

    const login = useCallback((userData) => {
        const name = userData.name || userData.username || null;

        setAuthState({
            userId: userData.id || userData.userId,
            role: userData.role,
            email: userData.email,
            name,
            isAuthenticated: true,
            isLoading: false,
        });

        const redirectTo = userData.role === 'candidate' ? '/events' : '/dashboard';
        navigate(redirectTo, { replace: true });
    }, [navigate]);

    const logout = useCallback(async () => {
    // 1. Hit the server FIRST so httpOnly cookies get cleared server-side
    try {
        await axios.post(`${serverURL}/auth/logout`);
    } catch (err) {
        if (err.response?.status !== 401) {
            console.error('Logout error:', err);
        }
    }

    // 2. Fetch active MSAL account BEFORE touching storage
    let activeAccount = null;
    try {
        activeAccount = msalInstance.getActiveAccount();
        if (!activeAccount) {
            const accounts = msalInstance.getAllAccounts();
            if (accounts.length > 0) {
                activeAccount = accounts[0];
            }
        }
    } catch (err) {
        console.error('MSAL account fetch error:', err);
    }

    // 3. Safely clear client storage (preserve MSAL cache)
    localStorage.clear();
    Object.keys(sessionStorage).forEach(key => {
        // Do not delete MSAL internal cache, otherwise logoutRedirect fails
        if (!key.startsWith('msal') && !key.includes('microsoft')) {
            sessionStorage.removeItem(key);
        }
    });

    // 4. Clear MSAL session if active
    if (activeAccount) {
        try {
            msalInstance.setActiveAccount(activeAccount);
            await msalInstance.logoutRedirect({ account: activeAccount });
            return; // Stop execution, browser will navigate away
        } catch (err) {
            console.error('MSAL logout error:', err);
        }
    }

    // 5. Fallback for regular (non-SSO) users
    setAuthState(unauthenticatedState);
    navigate('/login', { replace: true });
}, [navigate]);

    const value = useMemo(() => ({
        ...authState,
        login,
        logout,
        checkAuthStatus,
    }), [authState, login, logout, checkAuthStatus]);

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

AuthProvider.propTypes = {
    children: PropTypes.node.isRequired,
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
};

export default AuthContext;
